import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Users, Briefcase, Euro, ClipboardList, Building2, RefreshCw, CalendarPlus, Archive } from "lucide-react";
import { Button } from "../ui/button";
import { AdminSparkline } from "./AdminSparkline";
import { AdminIntegrationHealthCard } from "./AdminIntegrationHealthCard";
import { PendingAlert } from "./PendingAlert";

function KpiCard({ icon: Icon, label, value, sub, series, color, testId, onClick }) {
  const clickable = typeof onClick === "function";
  return (
    <div
      data-testid={testId}
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
      className={`bg-white rounded-xl border border-black/5 p-5 flex flex-col justify-between min-h-[140px] ${clickable ? "cursor-pointer hover:border-amber-300 hover:shadow-sm transition" : ""}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">{label}</p>
          <p className="font-heading font-black tracking-tighter text-3xl text-slate-900 mt-1">{value}</p>
          {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
        </div>
        <div className={`w-9 h-9 rounded-lg ${color} flex items-center justify-center`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
      </div>
      {series && series.length > 0 && (
        <div className="mt-3">
          <AdminSparkline data={series} color={color === "bg-amber-500" ? "#f59e0b" : color === "bg-emerald-500" ? "#10b981" : color === "bg-sky-500" ? "#0ea5e9" : color === "bg-fuchsia-500" ? "#d946ef" : "#f59e0b"} testId={`${testId}-sparkline`} />
        </div>
      )}
    </div>
  );
}

export function AdminOverviewSection({ pendingJobs, pendingCompanies, onGoJobs, onGoCompanies, onNavigate }) {
  const [stats, setStats] = useState(null);
  const [series, setSeries] = useState({});
  const [health, setHealth] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const [s, u, j, a, p, h] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/timeseries?type=users&days=30"),
        api.get("/admin/timeseries?type=jobs&days=30"),
        api.get("/admin/timeseries?type=applications&days=30"),
        api.get("/admin/timeseries?type=payments&days=30"),
        api.get("/admin/integrations/health"),
      ]);
      setStats(s.data);
      setSeries({ users: u.data.series, jobs: j.data.series, applications: a.data.series, payments: p.data.series });
      setHealth(h.data);
    } catch { /* ignore */ }
    finally { setRefreshing(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 data-testid="admin-overview-title" className="font-heading font-black tracking-tighter text-2xl text-slate-900">Visão geral</h2>
          <p className="text-sm text-slate-500">KPIs, tendências dos últimos 30 dias e estado das integrações.</p>
        </div>
        <Button data-testid="admin-overview-refresh" variant="outline" size="sm" className="rounded-full" onClick={load} disabled={refreshing}>
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> Atualizar
        </Button>
      </div>

      {(pendingJobs > 0 || pendingCompanies > 0) && (
        <PendingAlert
          pendingJobs={pendingJobs}
          pendingCompanies={pendingCompanies}
          onGoJobs={onGoJobs}
          onGoCompanies={onGoCompanies}
        />
      )}

      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <KpiCard testId="admin-stat-users" icon={Users} label="Utilizadores" value={stats.total_users} sub={`${stats.total_candidates} candidatos`} series={series.users} color="bg-amber-500" onClick={onNavigate ? () => onNavigate("users") : undefined} />
          <KpiCard testId="admin-stat-companies" icon={Building2} label="Empresas" value={stats.total_companies} sub={`${stats.pending_companies} pendentes`} color="bg-fuchsia-500" onClick={onGoCompanies} />
          <KpiCard testId="admin-stat-jobs" icon={Briefcase} label="Vagas ativas" value={stats.active_jobs} sub={`${stats.pending_jobs} em moderação`} series={series.jobs} color="bg-emerald-500" onClick={onNavigate ? () => onNavigate("moderation") : undefined} />
          <KpiCard testId="admin-stat-jobs-week" icon={CalendarPlus} label="Novas (7 dias)" value={stats.new_jobs_week ?? 0} sub={`${stats.new_jobs_day ?? 0} nas últimas 24h`} color="bg-sky-500" onClick={onNavigate ? () => onNavigate("moderation") : undefined} />
          <KpiCard testId="admin-stat-jobs-inactive" icon={Archive} label="Expiradas / inativas" value={stats.inactive_jobs ?? 0} sub={`${stats.closed_jobs ?? 0} fechadas · ${stats.rejected_jobs ?? 0} rejeitadas`} color="bg-slate-500" onClick={onNavigate ? () => onNavigate("moderation") : undefined} />
          <KpiCard testId="admin-stat-applications" icon={ClipboardList} label="Candidaturas" value={stats.total_applications} series={series.applications} color="bg-sky-500" onClick={onNavigate ? () => onNavigate("applications") : undefined} />
          <KpiCard testId="admin-stat-revenue" icon={Euro} label="Receita" value={`${(stats.revenue_cents / 100).toFixed(0)}€`} sub="Total pago" series={series.payments?.map(s => ({ ...s, count: s.count / 100 }))} color="bg-amber-500" onClick={onNavigate ? () => onNavigate("boosts") : undefined} />
        </div>
      )}

      {health && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Integrações — estado ao vivo</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {health.integrations.map((integ) => (
              <AdminIntegrationHealthCard key={integ.key} integ={integ} testId={`admin-integ-${integ.key.toLowerCase()}`} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
